import React from 'react';
import styled from '@emotion/styled';
import SEOHelmet from './SEOHelmet';

const PolicyContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  h1, h2, h3 {
    color: #3c006e;
  }

  h1 {
    margin-bottom: 1rem;
  }

  h2 {
    margin: 2rem 0 1rem;
  }

  h3 {
    margin: 1.5rem 0 1rem;
  }

  p {
    margin-bottom: 1rem;
    line-height: 1.6;
    color: #444;
  }

  ul {
    margin-bottom: 1rem;
    padding-left: 2rem;
    
    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
      color: #444;
    }
  }

  a {
    color: #3c006e;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

function PrivacyPolicy() {
  return (
    <>
      <SEOHelmet 
        title="Privacy Policy"
        description="Read Big Brain's privacy policy to understand how we collect, use, and protect your personal information."
        canonicalUrl="https://bigbrain.com/privacy-policy"
      />
      
      <PolicyContainer>
        <h1>Privacy Policy</h1>
        <p>Update date: March 25th, 2025</p>
        
        <p>Welcome to use Big Brain Things products and related services! Thank you for using our products and/or services (hereinafter referred to as "Services"). By using our Services, you are agreeing to this Policy. It is our priority to ensure the safety and privacy of our users ("user" or "you"), and we will do our best to protect your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you access our applications and services. Please read this Privacy Policy carefully. IF YOU DO NOT AGREE WITH THE TERMS OF THIS PRIVACY POLICY, PLEASE DO NOT ACCESS THE APPLICATION.</p>
        
        <p>We reserve the right to make changes to this Privacy Policy at any time and for any reason. We will alert you about any changes by updating the "Last updated" date of this Privacy Policy. You are encouraged to periodically review this Privacy Policy to stay informed of updates. You will be deemed to have been made aware of, will be subject to, and will be deemed to have accepted the changes in any revised Privacy Policy by your continued use of the Application after the date such revised Privacy Policy is posted.</p>
        
        <h2>1. Collection of Information</h2>
        <p>We may collect necessary information about your usage in a variety of ways. The information we may collect is for different purposes, including:</p>
        
        <h3>1.1 To enable you to use the app and to provide you with its functionalities</h3>
        <p>We collect identifiers and internet and network activity information (such as IP address, device model, device type, OS version, device language, device name, country set in the device settings, information about your interactions with the app, and unique identifiers), and other information necessary to enable you to use the app.</p>
        
        <h3>1.2 AI Image Generation and Processing</h3>
        <p>For our AI image generation service, particularly when processing facial images:</p>
        <ul>
          <li>We temporarily store your uploaded facial images while processing them through our third-party AI image generation API</li>
          <li>Your original facial images are automatically deleted from our servers immediately after the AI generation process is complete</li>
          <li>The AI-generated images are stored in your personal inventory for your access</li>
          <li>We ensure secure transmission of images to and from our third-party API partners</li>
          <li>Our third-party API partners are bound by strict data protection agreements</li>
        </ul>
        
        <h3>1.3 Video Processing and Effects (effect-01 App)</h3>
        <p>For our video effects processing service in the effect-01 app:</p>
        <ul>
          <li>We temporarily store your uploaded videos while processing them through our video effects algorithms</li>
          <li>Your original videos are automatically deleted from our servers immediately after the effects processing is complete</li>
          <li>The processed videos with applied effects are stored in your personal inventory for your access</li>
          <li>Processed videos are stored on our backend servers for a period of 12 hours to ensure you have sufficient time to download them</li>
          <li>After 12 hours, processed videos are automatically deleted from our servers</li>
          <li>We ensure secure transmission of videos to and from our processing servers</li>
          <li>Video processing may utilize third-party AI and machine learning services for effects generation</li>
          <li>We do not use your video content for training our AI models or for any other purpose beyond providing the requested effects</li>
          <li>Video files are encrypted during transmission and storage to ensure maximum security</li>
        </ul>
        
        <h3>1.4 To improve our Services and quality</h3>
        <p>To ensure the quality of the services and improve our applications, we collect and analyze usage statistics, technical issues, and app performance data. These data are aggregated and do not track individual users.</p>
        
        <h2>2. Data Storage and Protection</h2>
        <p>Personal data may be processed by both automated and non-automated means and may be stored at our premises and on our service providers' servers. We adopt technical and organizational measures designed to prevent the loss, improper use, and alteration of your personal data.</p>
        
        <h2>3. Sharing of Information</h2>
        <p>We share information in the following ways:</p>
        <ul>
          <li>Third-party AI Processing: Your images are shared with our AI generation partners solely for the purpose of creating AI-generated images</li>
          <li>Video Effects Processing: Your videos are processed through our effects algorithms and may utilize third-party AI services solely for applying visual effects</li>
          <li>Service Improvement: Aggregated, non-personal data may be used to improve our services</li>
          <li>Legal Requirements: When required by law or to protect our legal rights</li>
        </ul>
        
        <h2>4. Video Content and Rights</h2>
        <p>Regarding video content uploaded to our effect-01 app:</p>
        <ul>
          <li>You retain all rights to your original video content</li>
          <li>You grant us a limited, temporary license to process your videos solely for applying the requested effects</li>
          <li>We do not claim ownership of your original videos or the processed videos with effects</li>
          <li>You are responsible for ensuring you have the necessary rights to upload and process any video content</li>
          <li>We reserve the right to refuse processing of videos that may violate our terms of service or applicable laws</li>
        </ul>
        
        <h2>5. Security of Information</h2>
        <p>We implement appropriate technical and organizational security measures to protect your personal information. However, please be aware that no method of electronic storage or transmission is 100% secure.</p>
        
        <h2>6. Your Rights</h2>
        <p>You have the right to:</p>
        <ul>
          <li>Access your personal data</li>
          <li>Request deletion of your data</li>
          <li>Object to processing of your data</li>
          <li>Request a copy of your data</li>
        </ul>
        
        <h2>7. Contact Us</h2>
        <p>If you have any questions about this Privacy Policy or our practices, please contact us at: <a href="mailto:privacy@bigbrain.com">@bigbrainthingsandsuch@gmail.com</a></p>
      </PolicyContainer>
    </>
  );
}

export default PrivacyPolicy; 